import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Layout, Button, Space, Toast, Card, InputNumber, Modal, Input, Switch, Select, Tooltip, Tabs, TabPane } from '@douyinfe/semi-ui';
import {
    IconImage,
    IconLink,
    IconUnlink,
    IconDelete,
    IconVideo,
    IconText,
    IconPlus,
    IconAlignLeft,
    IconAlignCenter,
    IconAlignRight,
    IconAlignTop,
    IconAlignBottom,
    IconAlignCenterVertical,
    IconBarChartVStroked,
    IconBarChartHStroked,
    IconCopy,
    IconLayers,
    IconPaste,
    IconPalette,
    IconCopyAdd,
    IconGallery,
    IconList,
    IconTransparentStroked,
    IconDownload,
    IconUndo,
    IconRedo
} from '@douyinfe/semi-icons';
import { ColorPicker } from '../../components/ColorPicker';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ImageSelector } from '../../components/ImageSelector';
import LinkSelector from '../../components/LinkSelector';
import DroppableCanvas from './DroppableCanvas';
import StyleEditor from './StyleEditor';
import styles from './index.module.scss';
import '../../styles/animations.scss';
import { copyToClipboard } from '../../utils/clipboard';
import html2canvas from 'html2canvas';

const { Header, Sider, Content } = Layout;
const { Option } = Select;

const SectionTplEditor = () => {
    const [config, setConfig] = useState({
        type: "section-tpl",
        i: "", // 主图片
        is: [], // 轮播图数组
        v: "", // 视频
        bgType: "color", // 背景类型: color, gradient, image, video, carousel
        bgColor: "#ffffff", // 背景颜色
        bgGradientType: "linear", // 背景渐变类型: linear, radial
        bgGradientStart: "#ffffff", // 背景渐变起始颜色
        bgGradientEnd: "#000000", // 背景渐变结束颜色
        bgGradientAngle: 0, // 背景渐变角度（仅线性渐变）
        w: 750, // 固定宽度
        h: 400, // 高度
        ex: {}, // 其他样式
        content: [] // 内容元素数组
    });

    // 历史记录管理
    const [history, setHistory] = useState([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [isUndoRedoAction, setIsUndoRedoAction] = useState(false);

    const [selectedElementIndex, setSelectedElementIndex] = useState(null);
    const [selectedElementIndices, setSelectedElementIndices] = useState([]);
    const [showElementList, setShowElementList] = useState(false); // 控制元素列表栏显示/隐藏

    const [isBackgroundSelected, setIsBackgroundSelected] = useState(false);
    const [jsonString, setJsonString] = useState("");
    const [copiedElements, setCopiedElements] = useState([]);
    const [imageSelectModalVisible, setImageSelectModalVisible] = useState(false);
    const canvasRef = useRef(null);
    const jsonTextareaRef = useRef(null);
    const textInputRef = useRef(null);
    const imageSelectorRef = useRef(null);

    // 添加历史记录的函数
    const addToHistory = useCallback((newConfig) => {
        if (isUndoRedoAction) return; // 避免撤销/重做时创建新历史记录

        setHistory(prev => {
            // 如果当前不在历史记录末尾，删除当前位置之后的所有记录
            const newHistory = prev.slice(0, historyIndex + 1);

            // 检查是否与最后一条记录相同
            const lastConfig = newHistory[newHistory.length - 1];
            if (lastConfig && JSON.stringify(lastConfig) === JSON.stringify(newConfig)) {
                return prev; // 相同则不保存
            }

            // 添加新记录
            newHistory.push(JSON.parse(JSON.stringify(newConfig)));

            // 限制历史记录数量
            if (newHistory.length > 50) {
                newHistory.shift();
                setHistoryIndex(49);
                return newHistory;
            } else {
                setHistoryIndex(newHistory.length - 1);
                return newHistory;
            }
        });
    }, [historyIndex, isUndoRedoAction]);

    // 撤销功能
    const handleUndo = useCallback(() => {
        if (historyIndex > 0 && history.length > 0) {
            const previousConfig = history[historyIndex - 1];
            if (previousConfig) {
                setIsUndoRedoAction(true);
                setConfig(previousConfig);
                setHistoryIndex(prev => prev - 1);
                // 清空选择状态
                setSelectedElementIndex(null);
                setSelectedElementIndices([]);
                setIsBackgroundSelected(false);
                Toast.success('撤销成功');
                setTimeout(() => setIsUndoRedoAction(false), 0);
            }
        } else {
            Toast.info('没有可撤销的操作');
        }
    }, [history, historyIndex]);

    // 重做功能
    const handleRedo = useCallback(() => {
        if (historyIndex < history.length - 1 && history.length > 0) {
            const nextConfig = history[historyIndex + 1];
            if (nextConfig) {
                setIsUndoRedoAction(true);
                setConfig(nextConfig);
                setHistoryIndex(prev => prev + 1);
                // 清空选择状态
                setSelectedElementIndex(null);
                setSelectedElementIndices([]);
                setIsBackgroundSelected(false);
                Toast.success('重做成功');
                setTimeout(() => setIsUndoRedoAction(false), 0);
            }
        } else {
            Toast.info('没有可重做的操作');
        }
    }, [history, historyIndex]);

    useEffect(() => {
        // 实时更新配置JSON
        const configString = JSON.stringify(config, null, 2);
        setJsonString(configString);
    }, [config]);

    // 初始化历史记录
    useEffect(() => {
        if (history.length === 0) {
            setHistory([JSON.parse(JSON.stringify(config))]);
            setHistoryIndex(0);
        }
    }, [config, history.length]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            // 撤销功能 Ctrl+Z
            if (event.ctrlKey && event.key === 'z' && !event.shiftKey) {
                event.preventDefault();
                handleUndo();
                return;
            }

            // 重做功能 Ctrl+Y 或 Ctrl+Shift+Z
            if ((event.ctrlKey && event.key === 'y') || (event.ctrlKey && event.shiftKey && event.key === 'Z')) {
                event.preventDefault();
                handleRedo();
                return;
            }

            // 删除功能
            if (event.key === 'Delete') {
                if (selectedElementIndices.length > 0) {
                    handleDeleteMultipleElements(selectedElementIndices);
                } else if (selectedElementIndex !== null) {
                    handleDeleteElement(selectedElementIndex);
                }
            }

            // Escape 键清空选择
            if (event.key === 'Escape') {
                setSelectedElementIndices([]);
                setSelectedElementIndex(null);
                setIsBackgroundSelected(false);
            }

            // 复制功能 Ctrl+C
            if (event.ctrlKey && event.key === 'c') {
                event.preventDefault();
                handleCopyElements();
            }

            // 粘贴功能 Ctrl+V
            if (event.ctrlKey && event.key === 'v') {
                event.preventDefault();
                handlePasteElements();
            }

            // 方向键调整位置
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
                event.preventDefault();
                const step = event.shiftKey ? 10 : 1;

                if (selectedElementIndices.length > 0) {
                    // 批量移动多个元素
                    handleBatchMove(selectedElementIndices, event.key, step);
                } else if (selectedElementIndex !== null) {
                    // 单个元素移动
                    const selectedElement = config.content[selectedElementIndex];
                    if (!selectedElement) return;

                    let newX = selectedElement.x;
                    let newY = selectedElement.y;

                    switch (event.key) {
                        case 'ArrowUp':
                            newY = Math.max(0, selectedElement.y - step);
                            break;
                        case 'ArrowDown':
                            newY = Math.min(config.h - selectedElement.h, selectedElement.y + step);
                            break;
                        case 'ArrowLeft':
                            newX = Math.max(0, selectedElement.x - step);
                            break;
                        case 'ArrowRight':
                            newX = Math.min(config.w - selectedElement.w, selectedElement.x + step);
                            break;
                    }

                    handleElementChange(selectedElementIndex, { x: newX, y: newY });
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [selectedElementIndex, selectedElementIndices, config?.content, config?.w, config?.h, copiedElements, handleUndo, handleRedo]);

    const handleConfigChange = (newConfigString) => {
        try {
            const newConfig = JSON.parse(newConfigString);
            setConfig(newConfig);
        } catch (error) {
            Toast.error('配置格式不正确');
            console.error('Error parsing config:', error);
        }
    };

    // 选择背景
    const handleSelectBackground = () => {
        setIsBackgroundSelected(true);
        setSelectedElementIndex(null);
        setSelectedElementIndices([]);
    };

    // 添加图片元素
    const handleAddImage = () => {
        const newImage = {
            i: "", // 图片URL
            x: 50,
            y: 50,
            w: 200,
            h: 200,
            l: "", // 点击链接
            a: "", // 动画类型
            animationDuration: 2, // 动画持续时间（秒）
            animationDelay: 0, // 动画延迟（秒）
            ex: {} // 其他样式
        };
        setConfig(prev => {
            const newConfig = {
                ...prev,
                content: [...prev.content, newImage]
            };

            const newIndex = prev.content.length;
            setSelectedElementIndex(newIndex);
            setIsBackgroundSelected(false);

            // 保存历史记录
            addToHistory(newConfig);
            return newConfig;
        });
    };

    // 添加文字元素
    const handleAddText = () => {
        const newText = {
            t: "请输入文字",
            x: 50,
            y: 50,
            w: 200,
            h: 50,
            l: "", // 点击链接
            a: "", // 动画类型
            animationDuration: 2, // 动画持续时间（秒）
            animationDelay: 0, // 动画延迟（秒）
            ex: {
                fontSize: "16px",
                color: "#000000",
                textAlign: "left",
                fontWeight: "normal"
            }
        };
        setConfig(prev => {
            const newConfig = {
                ...prev,
                content: [...prev.content, newText]
            };

            const newIndex = prev.content.length;
            setSelectedElementIndex(newIndex);
            setIsBackgroundSelected(false);

            // 保存历史记录
            addToHistory(newConfig);
            return newConfig;
        });
    };

    // 删除元素
    const handleDeleteElement = (index) => {
        const element = config.content[index];
        let deleteMessage = '确定要删除此元素吗？';
        let indicesToDelete = [index];

        // 如果是成组元素，询问是否删除整个组
        if (element.groupId) {
            const groupElements = config.content
                .map((item, idx) => item.groupId === element.groupId ? idx : -1)
                .filter(idx => idx !== -1);

            if (groupElements.length > 1) {
                indicesToDelete = groupElements;
                deleteMessage = `此元素属于一个组，确定要删除整个组(${groupElements.length}个元素)吗？`;
            }
        }

        Modal.confirm({
            title: '确认删除',
            content: deleteMessage,
            onOk: () => {
                // 按索引从大到小排序，避免删除时索引混乱
                const sortedIndices = [...indicesToDelete].sort((a, b) => b - a);

                setConfig(prev => {
                    let newContent = [...prev.content];
                    sortedIndices.forEach(deleteIndex => {
                        newContent.splice(deleteIndex, 1);
                    });
                    const newConfig = { ...prev, content: newContent };

                    // 保存历史记录
                    addToHistory(newConfig);
                    return newConfig;
                });

                // 清空选择状态
                setSelectedElementIndex(null);
                setSelectedElementIndices([]);
            },
        });
    };

    // 批量删除元素
    const handleDeleteMultipleElements = (indices) => {
        Modal.confirm({
            title: '确认删除',
            content: `确定要删除这 ${indices.length} 个元素吗？`,
            onOk: () => {
                const sortedIndices = [...indices].sort((a, b) => b - a);
                setConfig(prev => {
                    let newContent = [...prev.content];
                    sortedIndices.forEach(index => {
                        newContent.splice(index, 1);
                    });
                    const newConfig = { ...prev, content: newContent };

                    // 保存历史记录
                    addToHistory(newConfig);
                    return newConfig;
                });

                setSelectedElementIndices([]);
                setSelectedElementIndex(null);
            },
        });
    };

    // 批量移动元素
    const handleBatchMove = (indices, direction, step) => {
        setConfig(prev => {
            const newConfig = {
                ...prev,
                content: prev.content.map((item, index) => {
                    if (!indices.includes(index)) return item;

                    let newX = item.x;
                    let newY = item.y;

                    switch (direction) {
                        case 'ArrowUp':
                            newY = Math.max(0, item.y - step);
                            break;
                        case 'ArrowDown':
                            newY = Math.min(config.h - item.h, item.y + step);
                            break;
                        case 'ArrowLeft':
                            newX = Math.max(0, item.x - step);
                            break;
                        case 'ArrowRight':
                            newX = Math.min(config.w - item.w, item.x + step);
                            break;
                    }

                    return { ...item, x: newX, y: newY };
                })
            };

            // 使用防抖保存历史记录，因为移动操作可能很频繁
            setTimeout(() => {
                if (!isUndoRedoAction) {
                    addToHistory(newConfig);
                }
            }, 300);

            return newConfig;
        });
    };

    // 复制元素
    const handleCopyElements = () => {
        let elementsToCopy = [];

        if (selectedElementIndices.length > 0) {
            // 复制多个选中的元素
            elementsToCopy = selectedElementIndices.map(index => config.content[index]).filter(Boolean);
        } else if (selectedElementIndex !== null) {
            // 复制单个选中的元素
            const element = config.content[selectedElementIndex];
            if (element) {
                elementsToCopy = [element];
            }
        }

        if (elementsToCopy.length > 0) {
            // 深拷贝元素，避免引用问题
            const copiedData = JSON.parse(JSON.stringify(elementsToCopy));
            setCopiedElements(copiedData);
            // 直接粘贴复制的元素
            handlePasteElements(copiedData);
            //Toast.success(`已复制并粘贴 ${elementsToCopy.length} 个元素`);
        } else {
            Toast.info('请先选择要复制的元素');
        }
    };

    // 粘贴元素
    const handlePasteElements = (elementsToPaste = null) => {
        const elements = elementsToPaste || copiedElements;
        if (elements.length === 0) {
            Toast.info('剪贴板为空，请先复制元素');
            return;
        }

        // 计算粘贴偏移量，避免元素重叠
        const offset = 20;
        const newElements = elements.map(element => {
            const newElement = { ...element };

            // 计算新位置，确保不超出画布边界
            let newX = element.x + offset;
            let newY = element.y + offset;

            // 如果超出右边界，则向左偏移
            if (newX + element.w > config.w) {
                newX = Math.max(0, config.w - element.w);
            }

            // 如果超出下边界，则向上偏移
            if (newY + element.h > config.h) {
                newY = Math.max(0, config.h - element.h);
            }

            newElement.x = newX;
            newElement.y = newY;

            // 如果元素有groupId，需要生成新的groupId以避免与原组冲突
            if (newElement.groupId) {
                const timestamp = Date.now();
                const random = Math.floor(Math.random() * 1000);
                newElement.groupId = `group_${timestamp}_${random}`;
            }

            return newElement;
        });

        // 如果复制的是多个元素且它们原本是一个组，给它们分配相同的新groupId
        if (newElements.length > 1) {
            const originalGroupIds = [...new Set(elements.map(el => el.groupId).filter(Boolean))];
            if (originalGroupIds.length === 1) {
                // 所有元素都属于同一个组，为它们分配新的相同groupId
                const newGroupId = `group_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
                newElements.forEach(element => {
                    if (element.groupId) {
                        element.groupId = newGroupId;
                    }
                });
            }
        }

        // 添加到配置中
        setConfig(prev => {
            const newConfig = {
                ...prev,
                content: [...prev.content, ...newElements]
            };

            // 保存历史记录
            addToHistory(newConfig);
            return newConfig;
        });

        // 选中新粘贴的元素
        const startIndex = config.content.length;
        const newIndices = newElements.map((_, index) => startIndex + index);
        setSelectedElementIndices(newIndices);
        setSelectedElementIndex(newIndices[0]);
        setIsBackgroundSelected(false);

        Toast.success(`已粘贴 ${newElements.length} 个元素`);
    };

    // 切换元素选择状态（支持Ctrl多选）
    const handleElementSelect = (index, ctrlKey = false) => {
        const element = config.content[index];

        // 验证元素是否存在
        if (!element) {
            //console.warn(`Element at index ${index} does not exist`);
            // 清空选中状态
            setSelectedElementIndex(null);
            setSelectedElementIndices([]);
            setIsBackgroundSelected(false);
            return;
        }

        let indicesToSelect = [index];

        // 如果元素属于某个组，选择整个组
        if (element.groupId) {
            indicesToSelect = config.content
                .map((item, idx) => item.groupId === element.groupId ? idx : -1)
                .filter(idx => idx !== -1);
        }

        if (ctrlKey) {
            if (selectedElementIndices.includes(index)) {
                // 取消选择（如果是组，取消整个组）
                setSelectedElementIndices(prev => prev.filter(i => !indicesToSelect.includes(i)));
                if (selectedElementIndices.length === indicesToSelect.length) {
                    setSelectedElementIndex(null);
                }
            } else {
                // 添加到选择（如果是组，添加整个组）
                setSelectedElementIndices(prev => [...new Set([...prev, ...indicesToSelect])]);
                setSelectedElementIndex(index);
            }
            setIsBackgroundSelected(false);
        } else {
            // 检查是否在多选状态下点击已选中的元素
            if (selectedElementIndices.length > 1 && selectedElementIndices.includes(index)) {
                // 在多选状态下点击已选中的元素，只更新主选中元素，保持多选状态
                setSelectedElementIndex(index);
                setIsBackgroundSelected(false);
            } else {
                // 单选模式（如果是组，选择整个组）
                setSelectedElementIndex(index);
                setSelectedElementIndices(indicesToSelect);
                setIsBackgroundSelected(false);
            }
        }
    };

    // 对齐操作
    const handleAlign = (type) => {
        if (type.includes('distribute')) {
            if (selectedElementIndices.length < 3) {
                Toast.info('分布功能需要至少选择三个元素');
                return;
            }
        } else {
            if (selectedElementIndices.length < 2) {
                Toast.info('请至少选择两个元素');
                return;
            }
        }

        // 获取选中的元素，并处理成组元素
        const selectedElements = selectedElementIndices.map(i => config.content[i]);

        // 将成组元素作为整体处理，计算组的边界框
        const groups = new Map(); // groupId -> { elements: [], bounds: {} }
        const standaloneElements = []; // 不成组的元素

        selectedElements.forEach((element, idx) => {
            if (element.groupId) {
                if (!groups.has(element.groupId)) {
                    // 找到同组的所有元素
                    const groupElements = config.content
                        .map((item, index) => ({ ...item, index }))
                        .filter(item => item.groupId === element.groupId);

                    // 计算组的边界框
                    const minX = Math.min(...groupElements.map(el => el.x));
                    const maxX = Math.max(...groupElements.map(el => el.x + el.w));
                    const minY = Math.min(...groupElements.map(el => el.y));
                    const maxY = Math.max(...groupElements.map(el => el.y + el.h));

                    groups.set(element.groupId, {
                        elements: groupElements,
                        bounds: { x: minX, y: minY, w: maxX - minX, h: maxY - minY }
                    });
                }
            } else {
                standaloneElements.push({ ...element, index: selectedElementIndices[idx] });
            }
        });

        // 合并组和独立元素，用于对齐计算
        const itemsToAlign = [
            ...Array.from(groups.values()).map(group => ({
                type: 'group',
                bounds: group.bounds,
                elements: group.elements
            })),
            ...standaloneElements.map(el => ({
                type: 'element',
                bounds: { x: el.x, y: el.y, w: el.w, h: el.h },
                element: el
            }))
        ];

        let updates = {};

        switch (type) {
            case 'left':
                const minX = Math.min(...itemsToAlign.map(item => item.bounds.x));
                // 更新组内元素
                itemsToAlign.forEach(item => {
                    if (item.type === 'group') {
                        const offsetX = minX - item.bounds.x;
                        item.elements.forEach(el => {
                            updates[el.index] = { x: el.x + offsetX };
                        });
                    } else {
                        updates[item.element.index] = { x: minX };
                    }
                });
                break;
            case 'right':
                const maxRight = Math.max(...itemsToAlign.map(item => item.bounds.x + item.bounds.w));
                itemsToAlign.forEach(item => {
                    if (item.type === 'group') {
                        const offsetX = maxRight - (item.bounds.x + item.bounds.w);
                        item.elements.forEach(el => {
                            updates[el.index] = { x: el.x + offsetX };
                        });
                    } else {
                        updates[item.element.index] = { x: maxRight - item.element.w };
                    }
                });
                break;
            case 'top':
                const minY = Math.min(...itemsToAlign.map(item => item.bounds.y));
                itemsToAlign.forEach(item => {
                    if (item.type === 'group') {
                        const offsetY = minY - item.bounds.y;
                        item.elements.forEach(el => {
                            updates[el.index] = { y: el.y + offsetY };
                        });
                    } else {
                        updates[item.element.index] = { y: minY };
                    }
                });
                break;
            case 'bottom':
                const maxBottom = Math.max(...itemsToAlign.map(item => item.bounds.y + item.bounds.h));
                itemsToAlign.forEach(item => {
                    if (item.type === 'group') {
                        const offsetY = maxBottom - (item.bounds.y + item.bounds.h);
                        item.elements.forEach(el => {
                            updates[el.index] = { y: el.y + offsetY };
                        });
                    } else {
                        updates[item.element.index] = { y: maxBottom - item.element.h };
                    }
                });
                break;
            case 'centerX':
                const centerX = itemsToAlign.reduce((sum, item) => sum + item.bounds.x + item.bounds.w / 2, 0) / itemsToAlign.length;
                itemsToAlign.forEach(item => {
                    if (item.type === 'group') {
                        const targetCenterX = centerX - item.bounds.w / 2;
                        const offsetX = targetCenterX - item.bounds.x;
                        item.elements.forEach(el => {
                            updates[el.index] = { x: el.x + offsetX };
                        });
                    } else {
                        updates[item.element.index] = { x: centerX - item.element.w / 2 };
                    }
                });
                break;
            case 'centerY':
                const centerY = itemsToAlign.reduce((sum, item) => sum + item.bounds.y + item.bounds.h / 2, 0) / itemsToAlign.length;
                itemsToAlign.forEach(item => {
                    if (item.type === 'group') {
                        const targetCenterY = centerY - item.bounds.h / 2;
                        const offsetY = targetCenterY - item.bounds.y;
                        item.elements.forEach(el => {
                            updates[el.index] = { y: el.y + offsetY };
                        });
                    } else {
                        updates[item.element.index] = { y: centerY - item.element.h / 2 };
                    }
                });
                break;
            case 'distributeX':
                // 水平分布：按中心点排序，然后均匀分布
                const sortedByX = itemsToAlign
                    .map((item, idx) => ({ item, idx }))
                    .sort((a, b) => (a.item.bounds.x + a.item.bounds.w / 2) - (b.item.bounds.x + b.item.bounds.w / 2));

                if (sortedByX.length >= 3) {
                    const leftmostCenterX = sortedByX[0].item.bounds.x + sortedByX[0].item.bounds.w / 2;
                    const rightmostCenterX = sortedByX[sortedByX.length - 1].item.bounds.x + sortedByX[sortedByX.length - 1].item.bounds.w / 2;
                    const spacing = (rightmostCenterX - leftmostCenterX) / (sortedByX.length - 1);

                    sortedByX.forEach(({ item }, idx) => {
                        if (idx > 0 && idx < sortedByX.length - 1) {
                            const targetCenterX = leftmostCenterX + spacing * idx;
                            if (item.type === 'group') {
                                const offsetX = targetCenterX - (item.bounds.x + item.bounds.w / 2);
                                item.elements.forEach(el => {
                                    updates[el.index] = { x: el.x + offsetX };
                                });
                            } else {
                                updates[item.element.index] = { x: targetCenterX - item.element.w / 2 };
                            }
                        }
                    });
                }
                break;
            case 'distributeY':
                // 垂直分布：按中心点排序，然后均匀分布
                const sortedByY = itemsToAlign
                    .map((item, idx) => ({ item, idx }))
                    .sort((a, b) => (a.item.bounds.y + a.item.bounds.h / 2) - (b.item.bounds.y + b.item.bounds.h / 2));

                if (sortedByY.length >= 3) {
                    const topmostCenterY = sortedByY[0].item.bounds.y + sortedByY[0].item.bounds.h / 2;
                    const bottommostCenterY = sortedByY[sortedByY.length - 1].item.bounds.y + sortedByY[sortedByY.length - 1].item.bounds.h / 2;
                    const spacing = (bottommostCenterY - topmostCenterY) / (sortedByY.length - 1);

                    sortedByY.forEach(({ item }, idx) => {
                        if (idx > 0 && idx < sortedByY.length - 1) {
                            const targetCenterY = topmostCenterY + spacing * idx;
                            if (item.type === 'group') {
                                const offsetY = targetCenterY - (item.bounds.y + item.bounds.h / 2);
                                item.elements.forEach(el => {
                                    updates[el.index] = { y: el.y + offsetY };
                                });
                            } else {
                                updates[item.element.index] = { y: targetCenterY - item.element.h / 2 };
                            }
                        }
                    });
                }
                break;
        }

        // 批量更新
        setConfig(prev => {
            const newConfig = {
                ...prev,
                content: prev.content.map((item, index) => {
                    if (updates[index]) {
                        return { ...item, ...updates[index] };
                    }
                    return item;
                })
            };

            // 保存历史记录
            addToHistory(newConfig);
            return newConfig;
        });

        if (type.includes('distribute')) {
            Toast.success('分布完成');
        } else {
            Toast.success('对齐完成');
        }
    };

    // 成组操作
    const handleGroup = () => {
        if (selectedElementIndices.length < 2) {
            Toast.info('请至少选择两个元素');
            return;
        }

        // 检查是否有元素已经属于其他组
        const alreadyGrouped = selectedElementIndices.some(index =>
            config.content[index].groupId
        );

        if (alreadyGrouped) {
            Toast.warning('选中的元素中有已成组的元素，请先取消成组');
            return;
        }

        const selectedElements = selectedElementIndices.map(i => config.content[i]);
        const groupId = `group_${Date.now()}`;

        // 为选中的元素添加组ID
        setConfig(prev => {
            const newConfig = {
                ...prev,
                content: prev.content.map((item, index) => {
                    if (selectedElementIndices.includes(index)) {
                        return { ...item, groupId };
                    }
                    return item;
                })
            };

            // 保存历史记录
            addToHistory(newConfig);
            return newConfig;
        });

        Toast.success(`已将 ${selectedElementIndices.length} 个元素成组。点击任一元素可选择整组，拖拽可整组移动`);
    };

    // 取消成组操作
    const handleUngroup = () => {
        if (selectedElementIndices.length === 0) {
            Toast.info('请选择要取消成组的元素');
            return;
        }

        setConfig(prev => {
            const newConfig = {
                ...prev,
                content: prev.content.map((item, index) => {
                    if (selectedElementIndices.includes(index) && item.groupId) {
                        const { groupId, ...itemWithoutGroup } = item;
                        return itemWithoutGroup;
                    }
                    return item;
                })
            };

            // 保存历史记录
            addToHistory(newConfig);
            return newConfig;
        });

        Toast.success('已取消成组');
    };

    // 层级操作 - 批量移动到顶层
    const handleBatchMoveToTop = () => {
        if (selectedElementIndices.length === 0) {
            Toast.info('请选择要移动的元素');
            return;
        }

        const sortedIndices = [...selectedElementIndices].sort((a, b) => a - b);
        setConfig(prev => {
            let newContent = [...prev.content];
            const elementsToMove = [];

            // 从后往前移除选中的元素
            for (let i = sortedIndices.length - 1; i >= 0; i--) {
                const index = sortedIndices[i];
                elementsToMove.unshift(newContent.splice(index, 1)[0]);
            }

            // 添加到数组末尾（顶层）
            newContent.push(...elementsToMove);

            const newConfig = { ...prev, content: newContent };

            // 保存历史记录
            addToHistory(newConfig);
            return newConfig;
        });

        // 更新选中的索引
        const newIndices = [];
        for (let i = 0; i < selectedElementIndices.length; i++) {
            newIndices.push(config.content.length - selectedElementIndices.length + i);
        }
        setSelectedElementIndices(newIndices);

        Toast.success('已移动到顶层');
    };

    // 层级操作 - 批量移动到底层
    const handleBatchMoveToBottom = () => {
        if (selectedElementIndices.length === 0) {
            Toast.info('请选择要移动的元素');
            return;
        }

        const sortedIndices = [...selectedElementIndices].sort((a, b) => b - a);
        setConfig(prev => {
            let newContent = [...prev.content];
            const elementsToMove = [];

            // 从后往前移除选中的元素
            for (let i = 0; i < sortedIndices.length; i++) {
                const index = sortedIndices[i];
                elementsToMove.push(newContent.splice(index, 1)[0]);
            }

            // 添加到数组开头（底层）
            newContent.unshift(...elementsToMove);

            const newConfig = { ...prev, content: newContent };

            // 保存历史记录
            addToHistory(newConfig);
            return newConfig;
        });

        // 更新选中的索引
        const newIndices = [];
        for (let i = 0; i < selectedElementIndices.length; i++) {
            newIndices.push(i);
        }
        setSelectedElementIndices(newIndices);

        Toast.success('已移动到底层');
    };

    // 更新元素属性
    const handleElementChange = useCallback((index, changes) => {
        setConfig(prev => {
            const newConfig = {
                ...prev,
                content: prev.content.map((item, i) => {
                    if (i === index) {
                        return { ...item, ...changes };
                    }
                    return item;
                })
            };

            // 使用防抖保存历史记录，避免频繁操作产生过多历史
            setTimeout(() => {
                if (!isUndoRedoAction) {
                    addToHistory(newConfig);
                }
            }, 500);

            return newConfig;
        });
    }, [addToHistory, isUndoRedoAction]);

    // 更新模块基础属性
    const handleModuleChange = useCallback((changes) => {
        setConfig(prev => {
            const newConfig = {
                ...prev,
                ...changes
            };

            // 使用防抖保存历史记录，避免频繁操作产生过多历史
            setTimeout(() => {
                if (!isUndoRedoAction) {
                    addToHistory(newConfig);
                }
            }, 500);

            return newConfig;
        });
    }, [addToHistory, isUndoRedoAction]);

    // 移动元素到顶层
    const moveElementToTop = (index) => {
        if (index === config.content.length - 1) {
            Toast.info('元素已在顶层');
            return;
        }

        setConfig(prev => {
            const newContent = [...prev.content];
            const element = newContent.splice(index, 1)[0];
            newContent.push(element);
            return {
                ...prev,
                content: newContent
            };
        });

        setSelectedElementIndex(config.content.length - 1);
        Toast.success('已移动到顶层');
    };

    // 移动元素到底层
    const moveElementToBottom = (index) => {
        if (index === 0) {
            Toast.info('元素已在底层');
            return;
        }

        setConfig(prev => {
            const newContent = [...prev.content];
            const element = newContent.splice(index, 1)[0];
            newContent.unshift(element);
            return {
                ...prev,
                content: newContent
            };
        });

        setSelectedElementIndex(0);
        Toast.success('已移动到底层');
    };

    // 处理双击元素
    const handleElementDoubleClick = (index, element) => {
        // 首先选中该元素
        setSelectedElementIndex(index);
        setSelectedElementIndices([index]);
        setIsBackgroundSelected(false);

        // 根据元素类型执行不同操作
        if (element.t !== undefined) {
            // 文字元素：聚焦到文字输入框
            setTimeout(() => {
                if (textInputRef.current) {
                    textInputRef.current.focus();
                    textInputRef.current.select();
                }
            }, 100);
        } else if (element.i !== undefined) {
            // 图片元素：打开图片选择器
            setTimeout(() => {
                setImageSelectModalVisible(true);
            }, 100);
        }
    };

    // 导出图片功能
    const handleExportImage = async () => {
        if (!canvasRef.current) {
            Toast.error('画布未找到');
            return;
        }

        Toast.info('正在生成图片...');

        // 备份当前状态
        const originalSelectedIndex = selectedElementIndex;
        const originalSelectedIndices = [...selectedElementIndices];
        const originalIsBackgroundSelected = isBackgroundSelected;

        try {
            // 清除选中状态
            setSelectedElementIndex(null);
            setSelectedElementIndices([]);
            setIsBackgroundSelected(false);

            // 添加导出模式CSS类来隐藏UI元素
            if (canvasRef.current) {
                canvasRef.current.classList.add('export-mode');
            }

            // 等待状态更新和CSS应用
            await new Promise(resolve => setTimeout(resolve, 150));

            const canvas = await html2canvas(canvasRef.current, {
                useCORS: true,
                allowTaint: true,
                backgroundColor: null,
                scale: 2, // 提高导出图片质量
                width: config.w,
                height: config.h,
                scrollX: 0,
                scrollY: 0,
                logging: false, // 减少控制台日志
                ignoreElements: (element) => {
                    // 忽略编辑器UI元素：选中边框、成组指示器、调整手柄等
                    return element.classList.contains('resize-handle') ||
                        element.classList.contains('group-indicator') ||
                        element.classList.contains('selection-box') ||
                        element.getAttribute('data-export-ignore') === 'true';
                }
            });

            // 创建下载链接
            const link = document.createElement('a');
            link.download = `section-template-${Date.now()}.png`;
            link.href = canvas.toDataURL('image/png', 1.0);

            // 触发下载
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            Toast.success('图片导出成功！');
        } catch (error) {
            console.error('导出图片失败:', error);
            Toast.error('导出图片失败，请重试');
        } finally {
            // 移除导出模式CSS类
            if (canvasRef.current) {
                canvasRef.current.classList.remove('export-mode');
            }

            // 恢复原始选中状态
            setSelectedElementIndex(originalSelectedIndex);
            setSelectedElementIndices(originalSelectedIndices);
            setIsBackgroundSelected(originalIsBackgroundSelected);
        }
    };

    const selectedElement = selectedElementIndex !== null ? config.content[selectedElementIndex] : null;

    return (
        <DndProvider backend={HTML5Backend}>
            <Layout className={styles.editorContainer}>
                <Layout>
                    <Content className={styles.content}>
                        {(
                            <div className={styles.toolbarContainer}>
                                <Card
                                    style={{ marginBottom: '16px', minHeight: '46px' }}
                                    bodyStyle={{ padding: '8px 12px' }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                        {/* 撤销重做工具 */}
                                        <Tooltip content="撤销 (Ctrl+Z)">
                                            <Button
                                                size="small"
                                                type="outline"
                                                icon={<IconUndo />}
                                                onClick={handleUndo}
                                                disabled={historyIndex <= 0 || history.length === 0}
                                                style={{ minWidth: '32px', height: '28px' }}
                                            />
                                        </Tooltip>
                                        <Tooltip content="重做 (Ctrl+Y)">
                                            <Button
                                                size="small"
                                                type="outline"
                                                icon={<IconRedo />}
                                                onClick={handleRedo}
                                                disabled={historyIndex >= history.length - 1 || history.length === 0}
                                                style={{ minWidth: '32px', height: '28px' }}
                                            />
                                        </Tooltip>

                                        <div style={{ width: '1px', height: '20px', backgroundColor: '#d9d9d9', margin: '0 4px' }} />

                                        {/* 添加工具 */}
                                        <Tooltip content="添加图片">
                                            <Button
                                                size="small"
                                                type="primary"
                                                icon={<IconImage />}
                                                onClick={handleAddImage}
                                                style={{ minWidth: '32px', height: '28px' }}
                                            />
                                        </Tooltip>
                                        <Tooltip content="添加文字">
                                            <Button
                                                size="small"
                                                type="primary"
                                                icon={<IconText />}
                                                onClick={handleAddText}
                                                style={{ minWidth: '32px', height: '28px' }}
                                            />
                                        </Tooltip>
                                        {(selectedElementIndex !== null || selectedElementIndices.length > 0) && (
                                            <Tooltip content="删除">
                                                <Button
                                                    size="small"
                                                    type="danger"
                                                    icon={<IconDelete />}
                                                    onClick={() => {
                                                        if (selectedElementIndices.length > 0) {
                                                            handleDeleteMultipleElements(selectedElementIndices);
                                                        } else if (selectedElementIndex !== null) {
                                                            handleDeleteElement(selectedElementIndex);
                                                        }
                                                    }}
                                                    style={{ minWidth: '32px', height: '28px' }}
                                                />
                                            </Tooltip>
                                        )}

                                        {/* 复制粘贴工具 */}
                                        {(selectedElementIndex !== null || selectedElementIndices.length > 0) && (
                                            <>
                                                <Tooltip content="复制 (Ctrl+C)">
                                                    <Button
                                                        size="small"
                                                        type="outline"
                                                        icon={<IconCopy />}
                                                        onClick={handleCopyElements}
                                                        style={{ minWidth: '32px', height: '28px' }}
                                                    />
                                                </Tooltip>
                                            </>
                                        )}

                                        <div style={{ width: '1px', height: '20px', backgroundColor: '#d9d9d9', margin: '0 4px' }} />

                                        {/* 背景设置工具 */}
                                        <Tooltip content="模块设置">
                                            <Button
                                                size="small"
                                                type="outline"
                                                icon={<IconTransparentStroked />}
                                                onClick={handleSelectBackground}
                                                style={{ minWidth: '32px', height: '28px' }}
                                            />
                                        </Tooltip>

                                        {/* 元素列表切换按钮 */}
                                        <Tooltip content={showElementList ? "隐藏元素列表" : "查看元素列表"}>
                                            <Button
                                                size="small"
                                                type="outline"
                                                icon={<IconLayers />}
                                                onClick={() => setShowElementList(!showElementList)}
                                                style={{ minWidth: '32px', height: '28px' }}
                                            />
                                        </Tooltip>

                                        {/* 导出图片按钮 */}
                                        <Tooltip content="导出为图片">
                                            <Button
                                                size="small"
                                                type="outline"
                                                icon={<IconDownload />}
                                                onClick={handleExportImage}
                                                style={{ minWidth: '32px', height: '28px' }}
                                            />
                                        </Tooltip>


                                        <div style={{ width: '1px', height: '20px', backgroundColor: '#d9d9d9', margin: '0 4px' }} />

                                        {/* 对齐工具 */}
                                        {selectedElementIndices.length >= 2 && (
                                            <>
                                                <Tooltip content="左对齐">
                                                    <Button
                                                        size="small"
                                                        type="outline"
                                                        icon={<IconAlignLeft />}
                                                        onClick={() => handleAlign('left')}
                                                        style={{ minWidth: '32px', height: '28px' }}
                                                    />
                                                </Tooltip>
                                                <Tooltip content="水平居中">
                                                    <Button
                                                        size="small"
                                                        type="outline"
                                                        icon={<IconAlignCenter />}
                                                        onClick={() => handleAlign('centerX')}
                                                        style={{ minWidth: '32px', height: '28px' }}
                                                    />
                                                </Tooltip>
                                                <Tooltip content="右对齐">
                                                    <Button
                                                        size="small"
                                                        type="outline"
                                                        icon={<IconAlignRight />}
                                                        onClick={() => handleAlign('right')}
                                                        style={{ minWidth: '32px', height: '28px' }}
                                                    />
                                                </Tooltip>
                                                <Tooltip content="顶对齐">
                                                    <Button
                                                        size="small"
                                                        type="outline"
                                                        icon={<IconAlignTop />}
                                                        onClick={() => handleAlign('top')}
                                                        style={{ minWidth: '32px', height: '28px' }}
                                                    />
                                                </Tooltip>
                                                <Tooltip content="垂直居中">
                                                    <Button
                                                        size="small"
                                                        type="outline"
                                                        icon={<IconAlignCenterVertical />}
                                                        onClick={() => handleAlign('centerY')}
                                                        style={{ minWidth: '32px', height: '28px' }}
                                                    />
                                                </Tooltip>
                                                <Tooltip content="底对齐">
                                                    <Button
                                                        size="small"
                                                        type="outline"
                                                        icon={<IconAlignBottom />}
                                                        onClick={() => handleAlign('bottom')}
                                                        style={{ minWidth: '32px', height: '28px' }}
                                                    />
                                                </Tooltip>

                                                <div style={{ width: '1px', height: '20px', backgroundColor: '#d9d9d9', margin: '0 4px' }} />
                                            </>
                                        )}

                                        {/* 分布工具 */}
                                        {selectedElementIndices.length >= 3 && (
                                            <>
                                                <Tooltip content="水平分布">
                                                    <Button
                                                        size="small"
                                                        type="outline"
                                                        icon={<IconBarChartVStroked />}
                                                        onClick={() => handleAlign('distributeX')}
                                                        style={{ minWidth: '32px', height: '28px' }}
                                                    />
                                                </Tooltip>
                                                <Tooltip content="垂直分布">
                                                    <Button
                                                        size="small"
                                                        type="outline"
                                                        icon={<IconBarChartHStroked />}
                                                        onClick={() => handleAlign('distributeY')}
                                                        style={{ minWidth: '32px', height: '28px' }}
                                                    />
                                                </Tooltip>

                                                <div style={{ width: '1px', height: '20px', backgroundColor: '#d9d9d9', margin: '0 4px' }} />
                                            </>
                                        )}

                                        {/* 成组工具 */}
                                        {selectedElementIndices.length >= 2 && (
                                            <>
                                                <Tooltip content="成组">
                                                    <Button
                                                        size="small"
                                                        type="outline"
                                                        icon={<IconLink />}
                                                        onClick={handleGroup}
                                                        style={{ minWidth: '32px', height: '28px' }}
                                                    />
                                                </Tooltip>
                                                <Tooltip content="取消成组">
                                                    <Button
                                                        size="small"
                                                        type="outline"
                                                        icon={<IconUnlink />}
                                                        onClick={handleUngroup}
                                                        style={{ minWidth: '32px', height: '28px' }}
                                                    />
                                                </Tooltip>

                                                <div style={{ width: '1px', height: '20px', backgroundColor: '#d9d9d9', margin: '0 4px' }} />
                                            </>
                                        )}

                                        {/* 层级工具 */}
                                        {selectedElementIndices.length >= 1 && (
                                            <>
                                                <Tooltip content="移到顶层">
                                                    <Button
                                                        size="small"
                                                        type="outline"
                                                        icon={<IconAlignTop />}
                                                        onClick={handleBatchMoveToTop}
                                                        style={{ minWidth: '32px', height: '28px' }}
                                                    />
                                                </Tooltip>
                                                <Tooltip content="移到底层">
                                                    <Button
                                                        size="small"
                                                        type="outline"
                                                        icon={<IconAlignBottom />}
                                                        onClick={handleBatchMoveToBottom}
                                                        style={{ minWidth: '32px', height: '28px' }}
                                                    />
                                                </Tooltip>
                                            </>
                                        )}
                                    </div>
                                </Card>
                            </div>
                        )}

                        <div className={styles.canvasContainer}>
                            <DroppableCanvas
                                config={config}
                                selectedElementIndex={selectedElementIndex}
                                selectedElementIndices={selectedElementIndices}
                                onElementSelect={handleElementSelect}
                                onElementChange={handleElementChange}
                                onModuleChange={handleModuleChange}
                                onElementDoubleClick={handleElementDoubleClick}
                                canvasRef={canvasRef}
                            />
                        </div>
                        <Card
                            title="模块配置JSON"
                            headerStyle={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                            style={{ marginTop: '20px', width: '100%', display: showElementList ? 'block' : 'none' }}
                        >
                            <Space style={{ marginBottom: '16px' }}>
                                <Button
                                    icon="copy"
                                    onClick={() => {
                                        copyToClipboard(jsonString).then(() => {
                                            Toast.success('配置已复制');
                                        }).catch(err => {
                                            console.error('Failed to copy text: ', err);
                                            Toast.error('复制失败');
                                        });
                                    }}
                                >
                                    复制配置
                                </Button>
                                <Button
                                    icon="check"
                                    onClick={() => {
                                        try {
                                            handleConfigChange(jsonString);
                                            Toast.success('配置已应用');
                                        } catch (err) {
                                            console.error('Failed to apply config: ', err);
                                            Toast.error('应用配置失败');
                                        }
                                    }}
                                >
                                    应用配置
                                </Button>
                            </Space>
                            <textarea
                                ref={jsonTextareaRef}
                                value={jsonString}
                                onChange={(e) => setJsonString(e.target.value)}
                                style={{
                                    width: '100%',
                                    height: '400px',
                                    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                                    fontSize: '14px',
                                    lineHeight: '1.4',
                                    padding: '12px',
                                    border: '1px solid #d9d9d9',
                                    borderRadius: '6px',
                                    resize: 'vertical',
                                    backgroundColor: '#fafafa'
                                }}
                                placeholder="模块配置JSON"
                            />
                        </Card>
                    </Content>
                    <Sider className={styles.sider}>

                        {selectedElement && (
                            <Card title="当前元素设置">
                                <div style={{ padding: '20px' }}>
                                    {selectedElement.i !== undefined ? (
                                        // 图片元素设置
                                        <div>
                                            <div style={{ marginBottom: '16px' }}>
                                                <label>选择图片:</label>
                                                <ImageSelector
                                                    ref={imageSelectorRef}
                                                    value={selectedElement.i}
                                                    onChange={value => {
                                                        handleElementChange(selectedElementIndex, { i: value });
                                                        setImageSelectModalVisible(false);
                                                    }}
                                                    visible={imageSelectModalVisible}
                                                    onClose={() => setImageSelectModalVisible(false)}
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        // 文字元素设置
                                        <div>
                                            <div style={{ marginBottom: '16px' }}>
                                                <label>文字内容:</label>
                                                <Input
                                                    ref={textInputRef}
                                                    key={`text-input-${selectedElementIndex}`}
                                                    value={selectedElement.t}
                                                    onChange={value => handleElementChange(selectedElementIndex, { t: value })}
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* 通用属性设置 */}
                                    <Tabs defaultActiveKey="basic" size="small">
                                        <TabPane tab="位置大小" itemKey="basic">
                                            <div style={{ marginBottom: '16px', display: 'flex', gap: '8px' }}>
                                                <div style={{ flex: 1 }}>
                                                    <label>宽度:</label>
                                                    <InputNumber
                                                        value={selectedElement.w}
                                                        onChange={value => handleElementChange(selectedElementIndex, { w: value })}
                                                        style={{ width: '100%' }}
                                                        precision={0}
                                                        min={1}
                                                    />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <label>高度:</label>
                                                    <InputNumber
                                                        value={selectedElement.h}
                                                        onChange={value => handleElementChange(selectedElementIndex, { h: value })}
                                                        style={{ width: '100%' }}
                                                        precision={0}
                                                        min={1}
                                                    />
                                                </div>
                                            </div>
                                            <div style={{ marginBottom: '16px', display: 'flex', gap: '8px' }}>
                                                <div style={{ flex: 1 }}>
                                                    <label>X坐标:</label>
                                                    <InputNumber
                                                        value={selectedElement.x}
                                                        onChange={value => handleElementChange(selectedElementIndex, { x: value })}
                                                        style={{ width: '100%' }}
                                                    />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <label>Y坐标:</label>
                                                    <InputNumber
                                                        value={selectedElement.y}
                                                        onChange={value => handleElementChange(selectedElementIndex, { y: value })}
                                                        style={{ width: '100%' }}
                                                    />
                                                </div>
                                            </div>
                                        </TabPane>

                                        <TabPane tab="点击事件" itemKey="interaction">
                                            <div style={{ marginBottom: '16px' }}>
                                                <label>点击链接:</label>
                                                <LinkSelector
                                                    value={selectedElement.l}
                                                    onChange={value => handleElementChange(selectedElementIndex, { l: value })}
                                                />
                                            </div>
                                            <div style={{ marginBottom: '16px' }}>
                                                <label>动画类型:</label>
                                                <Select
                                                    value={selectedElement.a}
                                                    onChange={value => handleElementChange(selectedElementIndex, { a: value })}
                                                    style={{ width: '100%' }}
                                                    placeholder="选择动画类型"
                                                >
                                                    <Option value="">无动画</Option>
                                                    <Option value="pulse">脉冲缩放</Option>
                                                    <Option value="bounce">上下弹跳</Option>
                                                    <Option value="swing">左右摇摆</Option>
                                                    <Option value="rotate">旋转</Option>
                                                    <Option value="wobble">水平摇摆</Option>
                                                    <Option value="heartbeat">心跳</Option>
                                                    <Option value="breathe">呼吸</Option>
                                                    <Option value="float">悬浮</Option>
                                                    <Option value="blink">闪烁</Option>
                                                    <Option value="shake">左右晃动</Option>
                                                    <Option value="rubberBand">橡皮筋</Option>
                                                </Select>
                                            </div>

                                            {selectedElement.a && (
                                                <>
                                                    <div style={{ marginBottom: '16px' }}>
                                                        <label>动画持续时间 (秒):</label>
                                                        <InputNumber
                                                            value={selectedElement.animationDuration || 2}
                                                            onChange={value => handleElementChange(selectedElementIndex, { animationDuration: value })}
                                                            style={{ width: '100%' }}
                                                            min={0.1}
                                                            max={10}
                                                            step={0.1}
                                                            precision={1}
                                                        />
                                                    </div>
                                                    <div style={{ marginBottom: '16px' }}>
                                                        <label>动画延迟 (秒):</label>
                                                        <InputNumber
                                                            value={selectedElement.animationDelay || 0}
                                                            onChange={value => handleElementChange(selectedElementIndex, { animationDelay: value })}
                                                            style={{ width: '100%' }}
                                                            min={0}
                                                            max={10}
                                                            step={0.1}
                                                            precision={1}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                        </TabPane>
                                    </Tabs>

                                    {/* 样式编辑器 */}
                                    <StyleEditor
                                        value={selectedElement.ex || {}}
                                        onChange={value => handleElementChange(selectedElementIndex, { ex: value })}
                                        elementType={selectedElement.i !== undefined ? 'image' : 'text'}
                                    />
                                </div>
                            </Card>
                        )}

                        {isBackgroundSelected && (
                            <Card title="模块设置">
                                <div style={{ padding: '20px' }}>
                                    <div style={{ marginBottom: '16px' }}>
                                        <label>背景类型:</label>
                                        <Select
                                            value={config.bgType}
                                            onChange={value => {
                                                // 切换背景类型时清空其他背景设置
                                                const updates = { bgType: value };
                                                if (value !== 'image') updates.i = "";
                                                if (value !== 'video') updates.v = "";
                                                if (value !== 'carousel') updates.is = [];
                                                if (value !== 'color') updates.bgColor = "#ffffff";
                                                if (value !== 'gradient') {
                                                    updates.bgGradientType = "linear";
                                                    updates.bgGradientStart = "#ffffff";
                                                    updates.bgGradientEnd = "#000000";
                                                    updates.bgGradientAngle = 0;
                                                }
                                                handleModuleChange(updates);
                                            }}
                                            style={{ width: '100%' }}
                                        >
                                            <Option value="color">颜色背景</Option>
                                            <Option value="gradient">渐变背景</Option>
                                            <Option value="image">图片背景</Option>
                                            <Option value="video">视频背景</Option>
                                            <Option value="carousel">轮播图背景</Option>
                                        </Select>
                                    </div>

                                    {config.bgType === 'color' && (
                                        <div style={{ marginBottom: '16px' }}>
                                            <label>背景颜色:</label>
                                            <ColorPicker
                                                value={config.bgColor}
                                                onChange={value => handleModuleChange({ bgColor: value })}
                                            />
                                        </div>
                                    )}

                                    {config.bgType === 'gradient' && (
                                        <>
                                            <div style={{ marginBottom: '16px' }}>
                                                <label>渐变类型:</label>
                                                <Select
                                                    value={config.bgGradientType}
                                                    onChange={value => handleModuleChange({ bgGradientType: value })}
                                                    style={{ width: '100%' }}
                                                >
                                                    <Option value="linear">线性渐变</Option>
                                                    <Option value="radial">径向渐变</Option>
                                                </Select>
                                            </div>
                                            <div style={{ marginBottom: '16px' }}>
                                                <label>起始颜色:</label>
                                                <ColorPicker
                                                    value={config.bgGradientStart}
                                                    onChange={value => handleModuleChange({ bgGradientStart: value })}
                                                />
                                            </div>
                                            <div style={{ marginBottom: '16px' }}>
                                                <label>结束颜色:</label>
                                                <ColorPicker
                                                    value={config.bgGradientEnd}
                                                    onChange={value => handleModuleChange({ bgGradientEnd: value })}
                                                />
                                            </div>
                                            {config.bgGradientType === 'linear' && (
                                                <div style={{ marginBottom: '16px' }}>
                                                    <label>渐变角度:</label>
                                                    <InputNumber
                                                        value={config.bgGradientAngle}
                                                        onChange={value => handleModuleChange({ bgGradientAngle: value })}
                                                        min={0}
                                                        max={360}
                                                        style={{ width: '100%' }}
                                                        addonAfter="°"
                                                    />
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {config.bgType === 'image' && (
                                        <div style={{ marginBottom: '16px' }}>
                                            <label>背景图片:</label>
                                            <ImageSelector
                                                value={config.i}
                                                onChange={value => handleModuleChange({ i: value })}
                                            />
                                        </div>
                                    )}

                                    {config.bgType === 'video' && (
                                        <div style={{ marginBottom: '16px' }}>
                                            <label>背景视频:</label>
                                            <Input
                                                value={config.v}
                                                onChange={value => handleModuleChange({ v: value })}
                                                placeholder="输入视频URL"
                                            />
                                        </div>
                                    )}

                                    {config.bgType === 'carousel' && (
                                        <div style={{ marginBottom: '16px' }}>
                                            <label>轮播图:</label>
                                            <div>
                                                {config.is.map((imageUrl, index) => (
                                                    <div key={index} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center' }}>
                                                        <ImageSelector
                                                            value={imageUrl}
                                                            onChange={value => {
                                                                const newIs = [...config.is];
                                                                newIs[index] = value;
                                                                handleModuleChange({ is: newIs });
                                                            }}
                                                        />
                                                        <Button
                                                            type="danger"
                                                            size="small"
                                                            onClick={() => {
                                                                const newIs = config.is.filter((_, i) => i !== index);
                                                                handleModuleChange({ is: newIs });
                                                            }}
                                                            style={{ marginLeft: '8px' }}
                                                        >
                                                            删除
                                                        </Button>
                                                    </div>
                                                ))}
                                                <Button
                                                    onClick={() => {
                                                        handleModuleChange({ is: [...config.is, ""] });
                                                    }}
                                                    style={{ width: '100%' }}
                                                >
                                                    添加轮播图
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                    <div style={{ marginBottom: '16px' }}>
                                        <label>模块高度:</label>
                                        <InputNumber
                                            value={config.h}
                                            onChange={value => handleModuleChange({ h: value })}
                                            style={{ width: '100%' }}
                                            min={100}
                                        />
                                    </div>
                                    <div style={{ marginBottom: '16px' }}>
                                        <StyleEditor
                                            value={config.ex || {}}
                                            onChange={value => handleModuleChange({ ex: value })}
                                            elementType="module"
                                        />
                                    </div>
                                </div>
                            </Card>
                        )}

                        <Card title="元素列表(点击快捷选择)" style={{ marginBottom: '16px', display: showElementList ? 'block' : 'none' }}>
                            <div style={{ padding: '20px' }}>
                                {/* 添加元素按钮 */}
                                <div style={{ marginBottom: '16px' }}>
                                    <Space style={{ width: '100%' }}>
                                        <Button
                                            onClick={handleAddImage}
                                            icon={<IconImage />}
                                            style={{ flex: 1 }}
                                            type="primary"
                                        >
                                            添加图片
                                        </Button>
                                        <Button
                                            onClick={handleAddText}
                                            icon={<IconText />}
                                            style={{ flex: 1 }}
                                            type="primary"
                                        >
                                            添加文字
                                        </Button>
                                    </Space>
                                </div>
                                <div className={styles.elementList}>
                                    {/* 背景选项 */}
                                    <div
                                        className={`${styles.elementItem} ${isBackgroundSelected ? styles.elementItemSelected : ''}`}
                                        onClick={handleSelectBackground}
                                    >
                                        <div className={styles.elementPreview}>
                                            {config.i ? (
                                                <img
                                                    src={config.i}
                                                    alt="背景"
                                                    className={styles.elementThumbnail}
                                                />
                                            ) : config.v ? (
                                                <div className={styles.videoElementPreview}>
                                                    V
                                                </div>
                                            ) : config.is.length > 0 ? (
                                                <div className={styles.slideshowElementPreview}>
                                                    S
                                                </div>
                                            ) : (
                                                <div className={styles.backgroundElementPreview}>
                                                    BG
                                                </div>
                                            )}
                                        </div>
                                        <div className={styles.elementInfo}>
                                            <div className={styles.elementName}>
                                                模块设置
                                                <span className={styles.elementIndex}>#BG</span>
                                            </div>
                                            <div className={styles.elementDetails}>
                                                {config.bgType === 'image' && '图片背景'}
                                                {config.bgType === 'video' && '视频背景'}
                                                {config.bgType === 'carousel' && `轮播背景(${config.is.length}张)`}
                                                {config.bgType === 'color' && '纯色背景'}
                                                {config.bgType === 'gradient' && `${config.bgGradientType === 'linear' ? '线性' : '径向'}渐变背景`}
                                            </div>
                                        </div>
                                    </div>

                                    {/* 元素列表 */}
                                    {config.content.length === 0 ? (
                                        <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                                            暂无元素，点击上方按钮添加
                                        </div>
                                    ) : (
                                        config.content.map((element, index) => (
                                            <div
                                                key={index}
                                                className={`${styles.elementItem} ${selectedElementIndices.includes(index) ? styles.elementItemSelected :
                                                    selectedElementIndex === index ? styles.elementItemSelected : ''
                                                    }`}
                                                onClick={(e) => {
                                                    handleElementSelect(index, e.ctrlKey || e.metaKey);
                                                }}
                                            >
                                                <div className={styles.elementPreview}>
                                                    {element.i !== undefined ? (
                                                        element.i ? (
                                                            <img
                                                                src={element.i}
                                                                alt="图片元素"
                                                                className={styles.elementThumbnail}
                                                            />
                                                        ) : (
                                                            <div className={styles.textElementPreview}>
                                                                IMG
                                                            </div>
                                                        )
                                                    ) : (
                                                        <div className={styles.textElementPreview}>
                                                            T
                                                        </div>
                                                    )}
                                                </div>
                                                <div className={styles.elementInfo}>
                                                    <div className={styles.elementName}>
                                                        {element.i !== undefined ? '图片元素' : '文字元素'}
                                                        <span className={styles.elementIndex}>#{index + 1}</span>
                                                        {element.groupId && (
                                                            <span className={styles.groupIndicator}>
                                                                组:{element.groupId.split('_')[1].slice(-3)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className={styles.elementDetails}>
                                                        {element.i !== undefined ? (
                                                            element.i ? `${element.w}×${element.h}` : '未设置图片'
                                                        ) : (
                                                            element.t ? `"${element.t.substring(0, 10)}${element.t.length > 10 ? '...' : ''}"` : '空文字'
                                                        )}
                                                        {element.groupId && (
                                                            <div style={{ color: '#22C55E', fontSize: '11px', marginTop: '2px' }}>
                                                                已成组 - 选择时会同时选中同组元素
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className={styles.elementActions}>
                                                    <Button
                                                        type="danger"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteElement(index);
                                                        }}
                                                    >
                                                        删除
                                                    </Button>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </Card>


                    </Sider>
                </Layout>
            </Layout>
        </DndProvider>
    );
};

export default SectionTplEditor;
